import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import TrackModal from './TrackModal';

interface Track {
  id: string;
  title: string;
  artist: string | null;
  cover_url: string | null;
  audio_url: string | null;
  soundcloud_url: string | null;
  duration: number | null;
  created_at: string;
}

export default function TracksManager() {
  const [tracks, setTracks] = useState<Track[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTrack, setEditingTrack] = useState<Track | null>(null);

  useEffect(() => {
    fetchTracks();
  }, []);

  const fetchTracks = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('tracks')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching tracks:', error);
    } else {
      setTracks(data || []);
    }
    setLoading(false);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar este track?')) return;

    const { error } = await supabase
      .from('tracks')
      .delete()
      .eq('id', id);

    if (error) {
      alert('Error al eliminar el track');
      console.error(error);
    } else {
      fetchTracks();
    }
  };

  const handleEdit = (track: Track) => {
    setEditingTrack(track);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingTrack(null);
    setShowModal(true);
  };

  const formatTime = (seconds: number | null) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white">Gestión de Tracks</h2>
        <button
          onClick={handleAdd}
          className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-white rounded-lg hover:shadow-lg hover:shadow-cyan-500/50 transition-all whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line mr-2"></i>
          Añadir Track
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : tracks.length === 0 ? (
        <div className="text-center py-12 bg-gray-900/50 rounded-lg border border-cyan-500/20">
          <i className="ri-music-2-line text-5xl text-gray-700 mb-4"></i>
          <p className="text-gray-500">No hay tracks. Añade tu primer mix.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tracks.map((track) => (
            <div
              key={track.id}
              className="bg-gray-900 border border-cyan-500/20 rounded-lg overflow-hidden hover:border-cyan-500/50 transition-all"
            >
              <div className="relative aspect-square">
                {track.cover_url ? (
                  <img
                    src={track.cover_url}
                    alt={track.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-cyan-500/20 to-fuchsia-500/20 flex items-center justify-center">
                    <i className="ri-music-2-line text-5xl text-cyan-400"></i>
                  </div>
                )}
              </div>

              <div className="p-4">
                <h3 className="text-lg font-bold text-white mb-1 truncate">{track.title}</h3>
                {track.artist && (
                  <p className="text-cyan-400 text-sm mb-2">{track.artist}</p>
                )}
                
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                  {track.duration && (
                    <span>
                      <i className="ri-time-line mr-1"></i>
                      {formatTime(track.duration)}
                    </span>
                  )}
                  {track.audio_url && (
                    <span className="bg-cyan-500/10 text-cyan-400 px-2 py-1 rounded">
                      Audio
                    </span>
                  )}
                  {track.soundcloud_url && (
                    <span className="bg-fuchsia-500/10 text-fuchsia-400 px-2 py-1 rounded">
                      SoundCloud
                    </span>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(track)}
                    className="flex-1 px-3 py-2 bg-cyan-500/10 text-cyan-400 rounded-lg hover:bg-cyan-500/20 transition-colors text-sm whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-edit-line mr-1"></i>
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(track.id)}
                    className="flex-1 px-3 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors text-sm whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-delete-bin-line mr-1"></i>
                    Eliminar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <TrackModal
          track={editingTrack}
          onClose={() => {
            setShowModal(false);
            setEditingTrack(null);
          }}
          onSave={fetchTracks}
        />
      )}
    </div>
  );
}